import { createFormContext, zodResolver } from '@mantine/form'
import { addDays } from 'date-fns'
import { useEffect, type HTMLAttributes } from 'react'

import {
  useMutationUpdateUserBookingsSettings,
  useQueryGetUserBookingsSettings,
  useQueryGetUserLogged,
} from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserBookingsSettings } from '@flecto/types'
import { useNotifications } from '@flecto/ui'
import { formatDateString } from '@flecto/utils'

import { useBookingHourState } from '../../hooks/useBookingHourState'
import { SectionBookingHours } from './SectionBookingHours'
import { SectionContracts } from './SectionContracts'
import { SectionDocuments } from './SectionDocuments'
import { SectionProforma } from './SectionProforma'
import { useProfileBookingsSchema } from './useProfileBookingsSchema'

export const [
  FormProfileBookingsProvider,
  useFormProfileBookingsContext,
  useFormProfileBookings,
] = createFormContext<CoreUserBookingsSettings>()

type FormProfileBookingsProps = HTMLAttributes<HTMLFormElement> & {}

export function FormProfileBookings({ ...props }: FormProfileBookingsProps) {
  const { t: tNotification } = useTranslation('saas', {
    keyPrefix: 'notifications',
  })

  const { data: user } = useQueryGetUserLogged()

  const { data } = useQueryGetUserBookingsSettings()
  const { default_end_hour, default_start_hour } = useBookingHourState()
  const { mutateAsync: updateUserBookingSettings } =
    useMutationUpdateUserBookingsSettings()
  const { showNotification } = useNotifications()

  let end = default_end_hour
  if (default_end_hour.getTime() < default_start_hour.getTime()) {
    end = addDays(default_end_hour, 1)
  }

  const formattedStart = formatDateString({
    date: default_start_hour.toString(),
    variant: 'time-brief',
  })

  const formattedEnd = formatDateString({
    date: end.toString(),
    variant: 'time-brief',
  })

  const { schema, initialValues } = useProfileBookingsSchema({
    payload: {
      default_booking_start_at: `2000-01-01T${formattedStart}:00`,
      default_booking_end_at: `2000-01-01T${formattedEnd}:00`,
      ...data,
    },
  })

  const form = useFormProfileBookings({
    initialValues,
    validate: zodResolver(schema),
  })

  const termsAndConditionsEmpty =
    !user?.terms_and_conditions ||
    user.terms_and_conditions === '<p></p>' ||
    user.terms_and_conditions === '\u003cp\u003e\u003c/p\u003e'

  const saveProfileBookings = async () => {
    try {
      await updateUserBookingSettings({
        payload: form.values,
      })

      showNotification({
        type: 'success',
        message: tNotification('profile_bookings.success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: tNotification('alert_error_general'),
      })
    }
  }

  useEffect(() => {
    form.setValues({
      default_booking_start_at: `2000-01-01T${formattedStart}:00`,
      default_booking_end_at: `2000-01-01T${formattedEnd}:00`,
    })
  }, [formattedStart, formattedEnd])

  useEffect(() => {
    form.setValues({
      ...data,
    })
  }, [data])

  useEffect(() => {
    if (!form.values.documents_quote_validity_period_active) {
      form.setFieldValue(
        'documents_quote_validity_period',
        Number(data?.documents_quote_validity_period) === 0
          ? 1
          : Number(data?.documents_quote_validity_period)
      )
    }
  }, [form.values.documents_quote_validity_period_active])

  useEffect(() => {
    if (termsAndConditionsEmpty) {
      form.setFieldValue(
        'documents_contract_include_terms_and_conditions',
        false
      )
      form.setFieldValue('documents_quote_include_terms_and_conditions', false)

      form.setFieldValue(
        'documents_proforma_include_terms_and_conditions',
        false
      )
    }
  }, [
    termsAndConditionsEmpty,
    form.values.documents_contract_include_terms_and_conditions,
    form.values.documents_quote_include_terms_and_conditions,
  ])

  return (
    <FormProfileBookingsProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveProfileBookings())}>
        <SectionBookingHours />
        <SectionDocuments termsAndConditionsEmpty={termsAndConditionsEmpty} />
        <SectionContracts termsAndConditionsEmpty={termsAndConditionsEmpty} />
        <SectionProforma termsAndConditionsEmpty={termsAndConditionsEmpty} />
      </form>
    </FormProfileBookingsProvider>
  )
}
